import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "declined-overlay"
}
const _hoisted_2 = { class: "declined-card" }
const _hoisted_3 = {
  key: 1,
  class: "audio-enable-overlay"
}
const _hoisted_4 = { class: "audio-enable-card" }
const _hoisted_5 = { class: "local-video-pip" }
const _hoisted_6 = { class: "controls-overlay" }
const _hoisted_7 = { class: "call-duration-display" }

import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useCallStore } from "../store/callStore";
import { usePatientStore } from "../store/patientStore";
import {
  useVideoClient,
  getZoomClientInstance,
  type SessionCredentials,
  type VideoProviderType,
} from "@virtual-care/shared";
import { callApiClient } from "../services/callApiClient";
import { signalingService } from "../services/signalingService";
import { callLogService } from "../services/callLogService";
import VideoDisplay from "../components/VideoDisplay.vue";
import CallControls from "../components/CallControls.vue";
import NotesModal from "../components/NotesModal.vue";

interface Props {
  sessionId: string;
}

const userId = "nurse-001";

// From POST /sessions/:id/join — stored in callStore during confirmCall (usePatientActions).
// If the route is opened directly (e.g. micro-frontend) or after refresh, store may be empty;
// beginCall() then POSTs join to fetch credentials before startCall.

export default /*@__PURE__*/_defineComponent({
  __name: 'ActiveCallView',
  props: {
    sessionId: {}
  },
  setup(__props: any) {

const props = __props;
const router = useRouter();
const callStore = useCallStore();
const patientStore = usePatientStore();

// Derive patientId from roomId stored in callStore (roomId alone is not PHI).
// Must be called BEFORE callStore.endCall() which nulls out roomId.
const refreshCallLog = () => {
  const roomId = callStore.roomId;
  if (!roomId) return;
  const patient = patientStore.patients.find((p) => p.room.roomId === roomId);
  if (patient) {
    patientStore.refreshPatientCallLog(patient.patientId).catch(() => {});
  }
};

const sessionCredentials = (callStore.credentials ?? {}) as SessionCredentials;
const providerType = (callStore.provider ?? "zoom") as VideoProviderType;

const {
  localStream,
  remoteStream,
  isConnected,
  error: webrtcError,
  startCall,
  endCall,
  toggleAudio: toggleAudioTrack,
  toggleVideo: toggleVideoTrack,
} = useVideoClient(props.sessionId, userId, "nurse", providerType, sessionCredentials, signalingService);

const showError = ref(false);
const isEnding = ref(false);
const declinedByPatient = ref(false);
const startTime = ref<number | null>(null);
const callDuration = ref("00:00");
let durationInterval: ReturnType<typeof setInterval> | null = null;

// Recording state (hidden per user request)
const isRecording = ref(false);
const recordingStatus = ref<"idle" | "recording" | "paused">("idle");

// Audio enable state (for page refresh scenarios)
const needsAudioEnable = ref(false);
const wasRestoredFromRefresh = ref(false);

// Notes modal state
const showNotesModal = ref(false);
const callWasAccepted = ref(false);
const patientName = ref('');
const sessionEndedRemotely = ref(false);

const statusColor = computed(() => {
  switch (callStore.status) {
    case "connecting":
      return "warning";
    case "connected":
      return "success";
    case "failed":
      return "error";
    default:
      return "info";
  }
});

const statusIcon = computed(() => {
  switch (callStore.status) {
    case "connecting":
      return "mdi-loading mdi-spin";
    case "connected":
      return "mdi-check-circle";
    case "failed":
      return "mdi-alert-circle";
    default:
      return "mdi-information";
  }
});

const statusText = computed(() => {
  switch (callStore.status) {
    case "initiating":
      return "Initiating call...";
    case "connecting":
      return "Connecting...";
    case "connected":
      return "Connected";
    case "failed":
      return "Connection failed";
    default:
      return "Unknown";
  }
});

const error = computed(() => webrtcError.value || callStore.error?.message);

function cleanup() {
  if (durationInterval) {
    clearInterval(durationInterval);
    durationInterval = null;
  }
}

function startDurationTimer() {
  startTime.value = Date.now();
  durationInterval = setInterval(() => {
    if (startTime.value) {
      const elapsed = Math.floor((Date.now() - startTime.value) / 1000);
      const m = Math.floor(elapsed / 60)
        .toString()
        .padStart(2, "0");
      const s = (elapsed % 60).toString().padStart(2, "0");
      callDuration.value = `${m}:${s}`;
    }
  }, 1000);
}

const onCallEnded = async () => {
  if (isEnding.value) return; // Already handling end from our side
  isEnding.value = true;
  
  console.log('[ActiveCallView] Patient ended call - ending video and showing notes modal');
  sessionEndedRemotely.value = true; // Mark that remote party ended it
  
  // Get patient info and call status BEFORE cleanup
  const patient = patientStore.patients.find(p => p.room.roomId === callStore.roomId);
  patientName.value = patient?.patientName || 'Patient';
  
  const callLog = await callLogService.getCallLogBySessionId(props.sessionId);
  callWasAccepted.value = callLog?.status === 'accepted' || callLog?.status === 'completed';
  
  // End the video call immediately to save Zoom minutes
  cleanup();
  await endCall();
  
  // Refresh call log
  refreshCallLog();
  callStore.endCall();
  
  // NOW show notes modal (video call already ended)
  showNotesModal.value = true;
};

const onPatientDeclined = async () => {
  if (isEnding.value) return;
  isEnding.value = true;
  cleanup();
  refreshCallLog(); // capture roomId before endCall() nulls it
  await endCall();
  callStore.endCall();
  declinedByPatient.value = true;
  setTimeout(() => router.push("/nurse-console"), 3000);
};

// Recording functionality disabled (hidden per user request)
const setupRecordingListeners = async () => {
  // Set up recording event listeners (only for Zoom provider)
  if (providerType === "zoom") {
    try {
      const zoomClient = getZoomClientInstance();

      zoomClient.on("recording-started", () => {
        console.log("[ActiveCallView] Recording started");
        isRecording.value = true;
        recordingStatus.value = "recording";
      });

      zoomClient.on("recording-stopped", () => {
        console.log("[ActiveCallView] Recording stopped");
        isRecording.value = false;
        recordingStatus.value = "idle";
      });

      zoomClient.on("recording-paused", () => {
        console.log("[ActiveCallView] Recording paused");
        recordingStatus.value = "paused";
      });

      zoomClient.on("recording-resumed", () => {
        console.log("[ActiveCallView] Recording resumed");
        recordingStatus.value = "recording";
      });
    } catch (err) {
      console.warn("[ActiveCallView] Failed to set up recording listeners:", err);
    }
  }
};


/** Ensure Zoom JWT + display name exist: use store if set, else POST /sessions/:id/join */
async function ensureSessionCredentials(): Promise<SessionCredentials> {
  const existing = callStore.credentials as SessionCredentials | undefined;
  if (existing?.token && typeof existing.token === "string" && existing.token.trim().length > 0) {
    return {
      ...existing,
      userName:
        typeof existing.userName === "string" && existing.userName.trim().length > 0 ? existing.userName : userId,
    };
  }

  // Credentials not in store - fetching from API (page refresh scenario)
  wasRestoredFromRefresh.value = true;
  
  const joinResponse = await callApiClient.joinSession(props.sessionId, "nurse");
  const session = joinResponse.session;
  const creds = session?.credentials as SessionCredentials | undefined;
  if (!creds?.token || typeof creds.token !== "string" || creds.token.trim().length === 0) {
    throw new Error("Missing session token from join response");
  }

  callStore.$patch({
    credentials: creds,
    ...(session?.roomId != null && session.roomId !== "" ? { roomId: session.roomId } : {}),
    ...(session?.provider != null ? { provider: session.provider as VideoProviderType } : {}),
  });

  return creds;
}

const beginCall = async () => {
  try {
    callStore.setStatus("connecting");
    const creds = await ensureSessionCredentials();
    await startCall(creds);
    callStore.setStatus("connected");
    startDurationTimer(); // Start call duration timer
    // await setupRecordingListeners(); // Recording disabled (hidden per user request)
    
    // If session was restored from refresh, prompt for audio enable
    if (wasRestoredFromRefresh.value) {
      needsAudioEnable.value = true;
    }
  } catch (err) {
    console.error("[ActiveCallView] Failed to start call:", err);
    callStore.setError("CALL_FAILED", "Failed to establish connection");
    showError.value = true;
  }
};

onMounted(async () => {
  signalingService.joinSession(props.sessionId);
  signalingService.on("call_ended", onCallEnded);
  signalingService.on("call_declined", onPatientDeclined);
  await beginCall();
});

onBeforeUnmount(() => {
  signalingService.off("call_ended", onCallEnded);
  signalingService.off("call_declined", onPatientDeclined);
  cleanup();
  if (!isEnding.value) {
    endCall();
  }
});

const toggleAudio = () => {
  toggleAudioTrack();
  callStore.toggleAudio();
};

const toggleVideo = () => {
  toggleVideoTrack();
  callStore.toggleVideo();
};

// Recording handlers disabled (hidden per user request)
const handleStartRecording = async () => {
  if (providerType !== "zoom") {
    console.warn("[ActiveCallView] Recording only supported for Zoom provider");
    return;
  }

  try {
    const zoomClient = getZoomClientInstance();
    await zoomClient.startRecording();
    console.log("[ActiveCallView] Recording start requested");
  } catch (err) {
    console.error("[ActiveCallView] Failed to start recording:", err);
    const errorMessage = err instanceof Error ? err.message : "Failed to start recording";
    callStore.setError("RECORDING_FAILED", errorMessage);
    showError.value = true;
  }
};

const handleStopRecording = async () => {
  if (providerType !== "zoom") {
    return;
  }

  try {
    const zoomClient = getZoomClientInstance();
    await zoomClient.stopRecording();
    console.log("[ActiveCallView] Recording stop requested");
  } catch (err) {
    console.error("[ActiveCallView] Failed to stop recording:", err);
    const errorMessage = err instanceof Error ? err.message : "Failed to stop recording";
    callStore.setError("RECORDING_FAILED", errorMessage);
    showError.value = true;
  }
};


const handleEndCall = async () => {
  if (isEnding.value) return;
  isEnding.value = true;
  
  // Get patient info and call status BEFORE ending call (while we still have roomId)
  const patient = patientStore.patients.find(p => p.room.roomId === callStore.roomId);
  patientName.value = patient?.patientName || 'Patient';
  
  const callLog = await callLogService.getCallLogBySessionId(props.sessionId);
  callWasAccepted.value = callLog?.status === 'accepted' || callLog?.status === 'completed';
  
  // End the video call immediately to save Zoom minutes
  cleanup();
  await endCall();
  
  // End the backend session
  try {
    await callApiClient.endSession(props.sessionId);
    signalingService.notifyCallEnded(props.sessionId, userId);
  } catch (err) {
    console.warn('[ActiveCallView] Session already ended:', err);
  }
  
  // Refresh call log AFTER ending session
  refreshCallLog();
  callStore.endCall();
  
  // NOW show notes modal (video call already ended)
  showNotesModal.value = true;
};

const handleNotesSaved = async (notes: string) => {
  // Update call log with notes
  const callLog = await callLogService.getCallLogBySessionId(props.sessionId);
  if (callLog) {
    await callLogService.updateCallLog(callLog.callId, {
      notes,
      notesEditedAt: new Date().toISOString(),
      notesEditedBy: userId,
    });
  }
  
  // Navigate away (call already ended)
  showNotesModal.value = false;
  router.push("/nurse-console");
};

const handleNotesSkipped = async () => {
  // Navigate away without notes (call already ended)
  showNotesModal.value = false;
  router.push("/nurse-console");
};

// completeEndCall is no longer needed - keeping for backwards compatibility
const completeEndCall = async () => {
  showNotesModal.value = false;
  router.push("/nurse-console");
};

async function handleEnableAudio() {
  try {
    console.log("[ActiveCallView] Nurse clicked Enable Audio button");
    const zoomClient = getZoomClientInstance();
    await zoomClient.unmuteAudio();
    console.log("[ActiveCallView] Audio started successfully");
    needsAudioEnable.value = false;
  } catch (err) {
    console.error("[ActiveCallView] Failed to enable audio:", err);
    callStore.setError("AUDIO_FAILED", "Failed to enable audio. Please try again.");
    showError.value = true;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    fluid: "",
    class: "active-call-view pa-0"
  }, {
    default: _withCtx(() => [
      (declinedByPatient.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_v_icon, {
                size: "64",
                color: "error",
                class: "mb-4"
              }, {
                default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                  _createTextVNode("mdi-phone-cancel", -1)
                ]))]),
                _: 1
              }),
              _cache[3] || (_cache[3] = _createElementVNode("p", { class: "declined-title" }, "Patient declined your call", -1)),
              _cache[4] || (_cache[4] = _createElementVNode("p", { class: "declined-subtitle" }, "Returning to room list…", -1)),
              _createVNode(_component_v_progress_linear, {
                indeterminate: "",
                color: "error",
                rounded: "",
                class: "mt-6",
                height: "4"
              })
            ])
          ]))
        : _createCommentVNode("", true),
      (needsAudioEnable.value && !declinedByPatient.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_v_icon, {
                size: "64",
                color: "primary",
                class: "mb-4"
              }, {
                default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                  _createTextVNode("mdi-volume-high", -1)
                ]))]),
                _: 1
              }),
              _cache[8] || (_cache[8] = _createElementVNode("p", { class: "audio-enable-title" }, "Enable Audio", -1)),
              _cache[9] || (_cache[9] = _createElementVNode("p", { class: "audio-enable-subtitle" }, "Audio was paused when the page refreshed. Click below to enable audio.", -1)),
              _createVNode(_component_v_btn, {
                color: "primary",
                size: "large",
                onClick: handleEnableAudio,
                class: "mt-4"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { start: "" }, {
                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                      _createTextVNode("mdi-volume-high", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[7] || (_cache[7] = _createTextVNode(" Enable Audio ", -1))
                ]),
                _: 1
              })
            ])
          ]))
        : (_openBlock(), _createBlock(_component_v_row, {
            key: 2,
            "no-gutters": "",
            class: "fill-height"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                class: "remote-video-container"
              }, {
                default: _withCtx(() => [
                  _createVNode(VideoDisplay, {
                    stream: _unref(remoteStream),
                    label: "Patient Room"
                  }, null, 8, ["stream"]),
                  _createVNode(_component_v_chip, {
                    class: "connection-status",
                    color: statusColor.value,
                    variant: "flat"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { start: "" }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(statusIcon.value), 1)
                        ]),
                        _: 1
                      }),
                      _createTextVNode(" " + _toDisplayString(statusText.value), 1)
                    ]),
                    _: 1
                  }, 8, ["color"]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(VideoDisplay, {
                      stream: _unref(localStream),
                      label: "You",
                      mirrored: true,
                      muted: true
                    }, null, 8, ["stream"])
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, _toDisplayString(callDuration.value), 1),
                    _createVNode(CallControls, {
                      "is-audio-enabled": _unref(callStore).isAudioEnabled,
                      "is-video-enabled": _unref(callStore).isVideoEnabled,
                      onToggleAudio: toggleAudio,
                      onToggleVideo: toggleVideo,
                      onEndCall: handleEndCall
                    }, null, 8, ["is-audio-enabled", "is-video-enabled"])
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          })),
      _createVNode(_component_v_dialog, {
        modelValue: showError.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showError).value = $event)),
        "max-width": "400"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                  _createTextVNode("Call Error", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(error.value), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: handleEndCall
                  }, {
                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                      _createTextVNode("End Call", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(NotesModal, {
        modelValue: showNotesModal.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showNotesModal).value = $event)),
        "call-was-accepted": callWasAccepted.value,
        "session-id": __props.sessionId,
        "patient-name": patientName.value,
        onNotesSaved: handleNotesSaved,
        onNotesSkipped: handleNotesSkipped
      }, null, 8, ["modelValue", "call-was-accepted", "session-id", "patient-name"])
    ]),
    _: 1
  }))
}
}

})